import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import SetupShell from '../components/setup/SetupShell';
import ProjectStep from '../components/setup/steps/ProjectStep';
import KeysStep from '../components/setup/steps/KeysStep';
import IntegrateStep from '../components/setup/steps/IntegrateStep';
import TestStep from '../components/setup/steps/TestStep';
import IntegrateAPI from '../components/setup/integrations/IntegrateAPI';
import IntegratePython from '../components/setup/integrations/IntegratePython';
import IntegrateCursor from '../components/setup/integrations/IntegrateCursor';
import IntegrateClaudeCode from '../components/setup/integrations/IntegrateClaudeCode';
import IntegrateOpenRouter from '../components/setup/integrations/IntegrateOpenRouter';
import { useUser } from '../contexts/UserContext';
import {
  SETUP_INTEGRATIONS,
  setupPathForStep,
  setupStepFromPath,
} from '../config/setupRoutes';

const INTEGRATION_COMPONENTS = {
  api: IntegrateAPI,
  python: IntegratePython,
  openrouter: IntegrateOpenRouter,
  cursor: IntegrateCursor,
  'claude-code': IntegrateClaudeCode,
};

export default function SetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { method } = useParams();
  const { userConfig, reloadUserConfig } = useUser();
  const [hasKeys, setHasKeys] = useState(false);
  const [hasEvents, setHasEvents] = useState(false);

  const step = setupStepFromPath(location.pathname);
  const apiUrl = userConfig?.env?.api_url || '';
  const tokenPlaceholder = sessionStorage.getItem('setup_latest_key') || 'YOUR_OSROUTER_KEY';

  const integration = SETUP_INTEGRATIONS.find((i) => i.slug === method);
  const IntegrationDetail = method ? INTEGRATION_COMPONENTS[method] : null;

  const footerRight = useMemo(() => {
    const btnClass = 'px-5 py-2.5 rounded-xl bg-theme-primary text-theme-button text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed';

    if (step === 1) {
      return (
        <button type="button" className={btnClass} onClick={() => navigate('/setup/keys/')}>
          Next
        </button>
      );
    }

    if (step === 2) {
      return (
        <button
          type="button"
          className={btnClass}
          disabled={!hasKeys}
          onClick={() => navigate('/setup/integrate/')}
        >
          Next
        </button>
      );
    }

    if (step === 3) {
      if (method && IntegrationDetail) {
        return (
          <button type="button" className={btnClass} onClick={() => navigate('/setup/test/')}>
            Next
          </button>
        );
      }
      return null;
    }

    if (step === 4) {
      return (
        <button
          type="button"
          className={btnClass}
          disabled={!hasEvents}
          onClick={() => {
            sessionStorage.removeItem('setup_latest_key');
            reloadUserConfig().finally(() => navigate('/'));
          }}
        >
          Finish
        </button>
      );
    }

    return null;
  }, [step, hasKeys, hasEvents, method, IntegrationDetail, navigate, reloadUserConfig]);

  const onFooterBack = () => {
    if (step === 3 && method) {
      navigate('/setup/integrate/');
      return;
    }
    if (step > 1) navigate(setupPathForStep(step - 1));
  };

  if (location.pathname === '/setup/' || location.pathname === '/setup') {
    return <Navigate to="/setup/project/" replace />;
  }

  if (step === 3 && method && !integration) {
    return <Navigate to="/setup/integrate/" replace />;
  }

  let content;
  if (step === 1) content = <ProjectStep />;
  else if (step === 2) content = <KeysStep onHasKeysChange={setHasKeys} />;
  else if (step === 3 && IntegrationDetail) {
    content = <IntegrationDetail apiUrl={apiUrl} tokenPlaceholder={tokenPlaceholder} />;
  } else if (step === 3) content = <IntegrateStep />;
  else if (step === 4) content = <TestStep onHasEventsChange={setHasEvents} />;

  return (
    <SetupShell
      step={step}
      footerShowBack={step > 1 || (step === 3 && !!method)}
      onFooterBack={onFooterBack}
      footerRight={footerRight}
    >
      {content}
    </SetupShell>
  );
}
